import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from "react-router"
import { getConsultReviewProducts } from "../../../../../services/history"
import { handlePromise } from "../../../../../utils/handlePromisse"
import { duplicateTopicConsultation } from "../../../../../services/decisionFlow"
import DuplicateConsultDialog, { DuplicatePatientState, initialState as dupInitialState } from "../../../history/modals/duplicate/duplicate-consult.dialog"
import VehiclesModel from "../../../../../models/vehicles.model"
import { SimpleSnackbar } from "../../../../../components/snackbar"
import { Loading } from "../../../../../components/loading"
import { ProductModel, productModelToDetails } from "../../../../../models/Product.model"
import { generatePdfForConsultations, openHtmlForPrint } from "../../../../../utils/pdf.util"
import ConsultationSummary, { SummaryActive } from "../../../history/components/ConsultationSummary"
import { ProductDetailDrawer } from "../products/product-detail-drawer"
import { Details } from "../products/product-detail"

type ReviewLocationState = { patientName?: string; focus?: string; date?: string; type?: string } | null

const formatConc = (value?: string): string => {
    if (!value) return "—"
    const n = String(value).replace(/%/g, "").trim()
    return n ? `${n}%` : "—"
}

const derivePeriod = (products: ProductModel[]): string | null => {
    const labels = new Set<string>()
    let day = false
    let night = false
    for (const p of products) {
        if (p.period && p.period.trim()) labels.add(p.period.trim())
        if (p.daytime) day = true
        if (p.nighttime) night = true
    }
    if (labels.size > 0) return Array.from(labels).join(" · ")
    if (day && night) return "Diurno e noturno"
    if (day) return "Diurno"
    if (night) return "Noturno"
    return null
}

export const ReviewPage: React.FC = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const locationState = (useLocation().state as ReviewLocationState) ?? null

    const [showSnackbar, setShowSnackbar] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [loading, setLoading] = useState(true)
    const [pdfLoading, setPdfLoading] = useState(false)
    const [nameConsultation, setNameConsultation] = useState<string>('')
    const [vehicles, setVehicles] = useState<VehiclesModel | null>(null)
    const [vehicleObservation, setVehicleObservation] = useState<string>('')
    const [observations, setObservations] = useState<string[]>([])
    const [products, setProducts] = useState<ProductModel[]>([])
    const [dupDialog, setDupDialog] = useState(dupInitialState)
    const [activeDetail, setActiveDetail] = useState<Details | null>(null)
    const [detailOpen, setDetailOpen] = useState(false)

    const onDuplicate = async (consultationId: number, patients: DuplicatePatientState[]) => {
        const [, error] = await handlePromise(duplicateTopicConsultation(consultationId, patients.map((p) => p.id)))
        setDupDialog(dupInitialState)
        setSnackbarMessage(error ? (error.response?.data?.message || 'Erro ao duplicar') : 'Consulta duplicada com sucesso!')
        setShowSnackbar(true)
    }

    const fetchProducts = useCallback(async () => {
        setLoading(true)
        const [result, error] = await handlePromise(getConsultReviewProducts(Number(id) || 0))
        setLoading(false)
        if (!!error) {
            setSnackbarMessage('Erro ao carregar a consulta')
            setShowSnackbar(true)
            return
        }
        setProducts(result.data.products as ProductModel[])
        if (result.data.vehicles !== null) {
            setVehicles(result.data.vehicles.model as VehiclesModel)
        }
        setVehicleObservation((result.data.observation || '').trim())
        setObservations(Array.isArray(result.data.observations) ? result.data.observations : [])
        if (result.data.name) {
            setNameConsultation(result.data.name)
        }
    }, [id])

    useEffect(() => {
        fetchProducts()
    }, [fetchProducts])

    const handleRecipe = useCallback(async () => {
        setPdfLoading(true)
        const result = await generatePdfForConsultations([{ consultationId: Number(id) || 0, type: 'TOPIC' }])
        if (!result.success) {
            setSnackbarMessage(result.error || 'Erro ao gerar PDF')
            setShowSnackbar(true)
            setPdfLoading(false)
            return
        }
        if (result.html) openHtmlForPrint(result.html)
        setPdfLoading(false)
    }, [id])

    const actives: SummaryActive[] = products.map((p) => ({
        name: p.productName || p.name || '—',
        fn: p.classes || p.mechanismAction || p.mechanism || '',
        conc: formatConc(p.percentage),
        detail: productModelToDetails(p) as Details,
    }))

    const handleViewActive = (active: SummaryActive) => {
        if (!active.detail) return
        setActiveDetail(active.detail)
        setDetailOpen(true)
    }

    const focus = locationState?.focus || nameConsultation

    return (
        <>
            <Loading open={pdfLoading} />
            <DuplicateConsultDialog
                open={dupDialog.open}
                consult={dupDialog.consult}
                onClose={() => setDupDialog(dupInitialState)}
                onDuplicate={onDuplicate}
            />
            <SimpleSnackbar show={showSnackbar} message={snackbarMessage} onDismiss={() => setShowSnackbar(false)} />

            <ConsultationSummary
                title={locationState?.patientName || nameConsultation}
                focus={focus}
                date={locationState?.date}
                typeLabel="Tópico"
                vehicle={vehicles?.name}
                vehicleObservation={vehicleObservation}
                period={derivePeriod(products)}
                actives={actives}
                observations={observations}
                loading={loading}
                onBack={() => navigate('/dashboard/history')}
                onDuplicate={() => setDupDialog({ open: true, consult: { id: Number(id) || 0, date: locationState?.date || '', name: focus, type: 'TOPIC' } })}
                onRecipe={handleRecipe}
                onViewActive={handleViewActive}
            />

            <ProductDetailDrawer
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                detail={activeDetail ?? ({} as Details)}
                typeLabel="Tópico"
            />
        </>
    )
}